// src/components/MCPMe.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../services/api";

/** ================== Types ================== */
type AccessStatus = "requested" | "approved";

interface HubKeyInfo {
  hub_auth_key: string;
  updated_at?: string;
}

interface MCPBrief {
  module_key: string;
  display_name?: string;
  description?: string;
  tags?: string[] | string | null; // 다양한 타입 수용
  owner_user_id: string;
  auto_approve?: boolean;
}

interface MyAccessItem {
  module_key: string;
  display_name?: string;
  description?: string;
  tags?: string[] | string | null;
  status: AccessStatus;
  requested_at: string;
  approved_at?: string | null;
  owner_user_id?: string;
}

interface IncomingRequest {
  module_key: string;
  requester_user_id: string;
  requested_at: string;
}

interface ApprovedMember {
  module_key: string;
  user_id: string;
  approved_at: string;
}

/** ================== Component ================== */
export default function MCPMe() {
  const navigate = useNavigate();

  /** ------- Auth header ------- */
  const authHeaders = useMemo(
    () => ({
      "x-user-id": localStorage.getItem("user_id") || "",
      "x-access-token": localStorage.getItem("access_token") || "",
      "x-refresh-token": localStorage.getItem("refresh_token") || "",
    }),
    []
  );

  /** ------- Global UI state ------- */
  const [globalLoading, setGlobalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  /** ------- Left Tabs ------- */
  // 0 = Hub Key, 1 = Own MCP, 2 = MCP Request
  const [leftTab, setLeftTab] = useState<number>(0);

  /** ------- Hub Key ------- */
  const [hub, setHub] = useState<HubKeyInfo | null>(null);
  const [loadingHub, setLoadingHub] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);

  /** ------- My Access (requested / approved) ------- */
  const [reqTab, setReqTab] = useState<AccessStatus>("requested");
  const [requestedList, setRequestedList] = useState<MyAccessItem[]>([]);
  const [approvedList, setApprovedList] = useState<MyAccessItem[]>([]);
  const [loadingRequested, setLoadingRequested] = useState(false);
  const [loadingApproved, setLoadingApproved] = useState(false);
  const [actingModuleKey, setActingModuleKey] = useState<string | null>(null);

  /** ------- Owner Console ------- */
  const [ownerModules, setOwnerModules] = useState<MCPBrief[]>([]);
  const [ownerRequests, setOwnerRequests] = useState<Record<string, IncomingRequest[]>>({});
  const [ownerApproved, setOwnerApproved] = useState<Record<string, ApprovedMember[]>>({});
  const [loadingOwnerModules, setLoadingOwnerModules] = useState(false);
  const [loadingOwnerReq, setLoadingOwnerReq] = useState<Record<string, boolean>>({});
  const [loadingOwnerAppr, setLoadingOwnerAppr] = useState<Record<string, boolean>>({});
  const [approvingUser, setApprovingUser] = useState<Record<string, string | null>>({});
  const [rejectingUser, setRejectingUser] = useState<Record<string, string | null>>({});
  const [evictingUser, setEvictingUser] = useState<Record<string, string | null>>({});

  /** ------- Confirm Dialog ------- */
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState("");
  const [confirmAction, setConfirmAction] = useState<null | (() => Promise<void>)>(null);

  const openConfirm = (msg: string, action: () => Promise<void>) => {
    setConfirmMsg(msg);
    setConfirmAction(() => action);
    setConfirmOpen(true);
  };

  const runConfirm = async () => {
    if (!confirmAction) return;
    setConfirmOpen(false);
    await confirmAction();
  };

  /** ================== Helpers ================== */
  const normalizeTags = (t: unknown): string[] => {
    if (Array.isArray(t)) return t.map(String);
    if (t == null) return [];
    if (typeof t === "string") {
      try {
        const parsed = JSON.parse(t);
        if (Array.isArray(parsed)) return parsed.map(String);
      } catch {
        const parts = t.split(",").map((s) => s.trim()).filter(Boolean);
        if (parts.length > 1) return parts;
        return t ? [t] : [];
      }
      return [t];
    }
    return [String(t)];
  };

  /** ================== Fetchers ================== */
  const fetchHubKey = async () => {
    setLoadingHub(true);
    try {
      const res = await axios.get(`${API_BASE}mcp/me/hub-key`, { headers: authHeaders });
      if (res.data?.success) {
        setHub(res.data.data || null);
      } else {
        throw new Error(res.data?.message || "Hub Key 조회 실패");
      }
    } catch (e: any) {
      setError(e?.message || "Hub Key를 불러오는 중 오류가 발생했습니다.");
    } finally {
      setLoadingHub(false);
    }
  };

  const regenerateHubKey = async () => {
    setRegenBusy(true);
    setError(null);
    try {
      const res = await axios.put(`${API_BASE}mcp/me/hub-key/regenerate`, {}, { headers: authHeaders });
      if (res.data?.success) {
        setHub(res.data.data || null);
        setInfo("Hub Key가 재발급되었습니다.");
      } else {
        throw new Error(res.data?.message || "Hub Key 재발급 실패");
      }
    } catch (e: any) {
      setError(e?.message || "Hub Key 재발급 중 오류가 발생했습니다.");
    } finally {
      setRegenBusy(false);
    }
  };

  const fetchMyAccess = async (status: AccessStatus) => {
    if (status === "requested") setLoadingRequested(true);
    else setLoadingApproved(true);

    try {
      const res = await axios.get(`${API_BASE}mcp/me/access`, {
        headers: authHeaders,
        params: { status },
      });
      if (res.data?.success) {
        const listRaw: MyAccessItem[] = res.data.data || [];
        const list = listRaw.map((it) => ({ ...it, tags: normalizeTags(it.tags as any) }));
        if (status === "requested") setRequestedList(list);
        else setApprovedList(list);
      } else {
        throw new Error(res.data?.message || "내 접근 목록 조회 실패");
      }
    } catch (e: any) {
      setError(e?.message || "내 접근 목록을 불러오는 중 오류가 발생했습니다.");
    } finally {
      if (status === "requested") setLoadingRequested(false);
      else setLoadingApproved(false);
    }
  };

  const fetchOwnerModules = async () => {
    setLoadingOwnerModules(true);
    try {
      const res = await axios.get(`${API_BASE}mcp/me/owner/modules`, { headers: authHeaders });
      if (res.data?.success) {
        const modsRaw: MCPBrief[] = res.data.data || [];
        const mods = modsRaw.map((m) => ({ ...m, tags: normalizeTags(m.tags as any) }));
        setOwnerModules(mods);
      } else {
        throw new Error(res.data?.message || "내 MCP(오너) 목록 조회 실패");
      }
    } catch (e: any) {
      setError(e?.message || "오너 MCP 목록을 불러오는 중 오류가 발생했습니다.");
    } finally {
      setLoadingOwnerModules(false);
    }
  };

  const fetchOwnerRequests = async (module_key: string) => {
    setLoadingOwnerReq((s) => ({ ...s, [module_key]: true }));
    try {
      const res = await axios.get(
        `${API_BASE}mcp/me/owner/${encodeURIComponent(module_key)}/access/requests`,
        { headers: authHeaders }
      );
      if (res.data?.success) {
        setOwnerRequests((s) => ({ ...s, [module_key]: res.data.data || [] }));
      } else {
        throw new Error(res.data?.message || "접근 요청 목록 조회 실패");
      }
    } catch (e: any) {
      setError(e?.message || "접근 요청 목록을 불러오는 중 오류가 발생했습니다.");
    } finally {
      setLoadingOwnerReq((s) => ({ ...s, [module_key]: false }));
    }
  };

  const fetchOwnerApproved = async (module_key: string) => {
    setLoadingOwnerAppr((s) => ({ ...s, [module_key]: true }));
    try {
      const res = await axios.get(
        `${API_BASE}mcp/me/owner/${encodeURIComponent(module_key)}/access/approved`,
        { headers: authHeaders }
      );
      if (res.data?.success) {
        setOwnerApproved((s) => ({ ...s, [module_key]: res.data.data || [] }));
      } else {
        throw new Error(res.data?.message || "승인된 사용자 목록 조회 실패");
      }
    } catch (e: any) {
      setError(e?.message || "승인된 사용자 목록을 불러오는 중 오류가 발생했습니다.");
    } finally {
      setLoadingOwnerAppr((s) => ({ ...s, [module_key]: false }));
    }
  };

  /** ================== Actions ================== */
  // My Access
  const cancelMyRequest = async (module_key: string) => {
    setActingModuleKey(module_key);
    try {
      const res = await axios.delete(
        `${API_BASE}mcp/me/access/${encodeURIComponent(module_key)}/request`,
        { headers: authHeaders }
      );
      if (res.data?.success) {
        await fetchMyAccess("requested");
        setInfo("접근 요청을 취소했습니다.");
      } else {
        throw new Error(res.data?.message || "요청 취소 실패");
      }
    } catch (e: any) {
      setError(e?.message || "요청 취소 중 오류가 발생했습니다.");
    } finally {
      setActingModuleKey(null);
    }
  };

  const leaveApprovedAccess = async (module_key: string) => {
    setActingModuleKey(module_key);
    try {
      const res = await axios.delete(
        `${API_BASE}mcp/me/access/${encodeURIComponent(module_key)}/access`,
        { headers: authHeaders }
      );
      if (res.data?.success) {
        await fetchMyAccess("approved");
        setInfo("해당 MCP 접근에서 탈퇴했습니다.");
      } else {
        throw new Error(res.data?.message || "접근 제거(탈퇴) 실패");
      }
    } catch (e: any) {
      setError(e?.message || "접근 제거(탈퇴) 중 오류가 발생했습니다.");
    } finally {
      setActingModuleKey(null);
    }
  };

  // Owner
  const approveUser = async (module_key: string, user_id: string) => {
    setApprovingUser((s) => ({ ...s, [module_key]: user_id }));
    try {
      const res = await axios.post(
        `${API_BASE}mcp/me/owner/${encodeURIComponent(module_key)}/access/${encodeURIComponent(
          user_id
        )}/approve`,
        {},
        { headers: authHeaders }
      );
      if (res.data?.success) {
        await Promise.all([fetchOwnerRequests(module_key), fetchOwnerApproved(module_key)]);
        setInfo(`승인 완료: ${user_id}`);
      } else {
        throw new Error(res.data?.message || "승인 실패");
      }
    } catch (e: any) {
      setError(e?.message || "승인 중 오류가 발생했습니다.");
    } finally {
      setApprovingUser((s) => ({ ...s, [module_key]: null }));
    }
  };

  const rejectUser = async (module_key: string, user_id: string) => {
    setRejectingUser((s) => ({ ...s, [module_key]: user_id }));
    try {
      const res = await axios.post(
        `${API_BASE}mcp/me/owner/${encodeURIComponent(module_key)}/access/${encodeURIComponent(
          user_id
        )}/reject`,
        {},
        { headers: authHeaders }
      );
      if (res.data?.success) {
        await Promise.all([fetchOwnerRequests(module_key), fetchOwnerApproved(module_key)]);
        setInfo(`거절 완료: ${user_id}`);
      } else {
        throw new Error(res.data?.message || "거절 실패");
      }
    } catch (e: any) {
      setError(e?.message || "거절 중 오류가 발생했습니다.");
    } finally {
      setRejectingUser((s) => ({ ...s, [module_key]: null }));
    }
  };

  const evictUser = async (module_key: string, user_id: string) => {
    setEvictingUser((s) => ({ ...s, [module_key]: user_id }));
    try {
      const res = await axios.delete(
        `${API_BASE}mcp/me/owner/${encodeURIComponent(module_key)}/access/${encodeURIComponent(
          user_id
        )}`,
        { headers: authHeaders }
      );
      if (res.data?.success) {
        await fetchOwnerApproved(module_key);
        setInfo(`추방 완료: ${user_id}`);
      } else {
        throw new Error(res.data?.message || "추방 실패");
      }
    } catch (e: any) {
      setError(e?.message || "추방 중 오류가 발생했습니다.");
    } finally {
      setEvictingUser((s) => ({ ...s, [module_key]: null }));
    }
  };

  /** ================== Effects ================== */
  // 초기 데이터 로딩
  useEffect(() => {
    (async () => {
      setGlobalLoading(true);
      setError(null);
      setInfo(null);
      await Promise.all([
        fetchHubKey(),
        fetchMyAccess("requested"),
        fetchMyAccess("approved"),
        fetchOwnerModules(),
      ]);
      setGlobalLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Own MCP 탭: 모듈 목록 로딩 후 각 모듈의 요청/승인 목록을 즉시 로딩(항상 펼침이므로)
  useEffect(() => {
    if (ownerModules.length === 0) return;
    ownerModules.forEach((m) => {
      if (!ownerRequests[m.module_key]) fetchOwnerRequests(m.module_key);
      if (!ownerApproved[m.module_key]) fetchOwnerApproved(m.module_key);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerModules]);

  /** ================== Render Helpers ================== */
  const LoadingBanner = (props: { text: string }) => (
    <Paper variant="outlined" sx={{ mb: 2 }}>
      <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <CircularProgress size={20} />
        <Typography>{props.text}</Typography>
      </Box>
      <LinearProgress />
    </Paper>
  );

  /** ================== Section UIs ================== */

  // Hub Key
  const HubKeySection = (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          내 Hub Key
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Hub Key 재발급">
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AutorenewIcon />}
                disabled={regenBusy || loadingHub}
                onClick={() =>
                  openConfirm("정말 Hub Key를 재발급할까요? Key는 즉시 무효화되고, 연결된 모든 서비스가 끊깁니다.", regenerateHubKey)
                }
              >
                {regenBusy ? "재발급 중..." : "재발급"}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <Box sx={{ mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Hub Key는 API 인증에 사용됩니다. 다른 사람과 공유하지 마세요.
        </Typography>
        <Paper variant="outlined" sx={{ mt: 1, p: 2, bgcolor: "#111" }}>
          {loadingHub && !hub ? (
            <Stack direction="row" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              <Typography>Hub Key 불러오는 중...</Typography>
            </Stack>
          ) : (
            <Typography sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
              {hub?.hub_auth_key || "—"}
            </Typography>
          )}
        </Paper>
      </Box>
    </Paper>
  );

  // MCP Request (내 접근 현황)
  const MyAccessSection = (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          내 접근 현황 (MCP)
        </Typography>
        <ToggleButtonGroup value={reqTab} exclusive onChange={(_, v) => v && setReqTab(v)} size="small">
          <ToggleButton value="requested">요청 중</ToggleButton>
          <ToggleButton value="approved">승인됨</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {reqTab === "requested" ? (
        <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: "#1e1e1e" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: "#ccc" }}>MCP</TableCell>
                <TableCell sx={{ color: "#ccc" }}>설명</TableCell>
                <TableCell sx={{ color: "#ccc" }}>태그</TableCell>
                <TableCell sx={{ color: "#ccc" }}>요청일</TableCell>
                <TableCell sx={{ color: "#ccc" }} align="right">
                  취소
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingRequested && requestedList.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ color: "#888", textAlign: "center", py: 4 }}>
                    <CircularProgress size={18} /> 불러오는 중...
                  </TableCell>
                </TableRow>
              )}
              {!loadingRequested && requestedList.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ color: "#888", textAlign: "center", py: 4 }}>
                    요청한 MCP가 없습니다.
                  </TableCell>
                </TableRow>
              )}
              {requestedList.map((it) => (
                <TableRow key={it.module_key} hover>
                  <TableCell sx={{ color: "#fff" }}>
                    <b>{it.display_name || it.module_key}</b>
                  </TableCell>
                  <TableCell sx={{ color: "#ccc" }}>{it.description || "—"}</TableCell>
                  <TableCell sx={{ color: "#ccc" }}>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {normalizeTags(it.tags).map((t, i) => (
                        <Chip key={`${it.module_key}-t-${i}`} label={t} size="small" />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ color: "#ccc" }}>{it.requested_at}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      disabled={actingModuleKey === it.module_key || loadingRequested}
                      onClick={() =>
                        openConfirm(
                          `요청을 취소할까요? (${it.display_name || it.module_key})`,
                          () => cancelMyRequest(it.module_key)
                        )
                      }
                    >
                      {actingModuleKey === it.module_key ? "취소 중..." : "요청 취소"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: "#1e1e1e" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: "#ccc" }}>MCP</TableCell>
                <TableCell sx={{ color: "#ccc" }}>설명</TableCell>
                <TableCell sx={{ color: "#ccc" }}>태그</TableCell>
                <TableCell sx={{ color: "#ccc" }}>승인일</TableCell>
                <TableCell sx={{ color: "#ccc" }} align="right">
                  접근 제거(탈퇴)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingApproved && approvedList.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ color: "#888", textAlign: "center", py: 4 }}>
                    <CircularProgress size={18} /> 불러오는 중...
                  </TableCell>
                </TableRow>
              )}
              {!loadingApproved && approvedList.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ color: "#888", textAlign: "center", py: 4 }}>
                    승인된 MCP가 없습니다.
                  </TableCell>
                </TableRow>
              )}
              {approvedList.map((it) => (
                <TableRow key={it.module_key} hover>
                  <TableCell sx={{ color: "#fff" }}>
                    <b>{it.display_name || it.module_key}</b>
                  </TableCell>
                  <TableCell sx={{ color: "#ccc" }}>{it.description || "—"}</TableCell>
                  <TableCell sx={{ color: "#ccc" }}>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {normalizeTags(it.tags).map((t, i) => (
                        <Chip key={`${it.module_key}-t-${i}`} label={t} size="small" />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ color: "#ccc" }}>{it.approved_at || "—"}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      disabled={actingModuleKey === it.module_key || loadingApproved}
                      onClick={() =>
                        openConfirm(
                          `이 MCP에서 탈퇴할까요? (${it.display_name || it.module_key})`,
                          () => leaveApprovedAccess(it.module_key)
                        )
                      }
                    >
                      {actingModuleKey === it.module_key ? "처리 중..." : "탈퇴"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );

  // Own MCP (오너 콘솔) – 항상 펼쳐진 상태 + 편집하기 버튼
  const OwnerSection = (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Own MCP (오너 콘솔)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        내가 만든 MCP의 접근 요청을 승인/거절하고, 승인된 사용자를 추방할 수 있습니다.
      </Typography>

      {loadingOwnerModules && <LoadingBanner text="오너 MCP 목록을 불러오는 중..." />}

      {ownerModules.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 2, color: "#888" }}>
          등록된 MCP가 없습니다.
        </Paper>
      ) : (
        ownerModules.map((m) => {
          const reqLoading = !!loadingOwnerReq[m.module_key];
          const apprLoading = !!loadingOwnerAppr[m.module_key];
          const reqs = ownerRequests[m.module_key] || [];
          const apprs = ownerApproved[m.module_key] || [];
          const approveBusy = approvingUser[m.module_key];
          const rejectBusy = rejectingUser[m.module_key];
          const evictBusy = evictingUser[m.module_key];

          return (
            <Paper key={m.module_key} variant="outlined" sx={{ mb: 2, p: 2, bgcolor: "#121212" }}>
              {/* Header (항상 펼침, 편집 버튼 제공) */}
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Stack spacing={0.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {m.display_name || m.module_key}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {m.description || "—"}
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                    {normalizeTags(m.tags).map((t, i) => (
                      <Chip key={`${m.module_key}-tag-${i}`} label={t} size="small" />
                    ))}
                    {typeof m.auto_approve === "boolean" && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={m.auto_approve ? "자동 승인 ON" : "자동 승인 OFF"}
                      />
                    )}
                  </Stack>
                </Stack>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/mcp/${m.module_key}`)}
                >
                  편집하기
                </Button>
              </Stack>

              {/* Pending Requests */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                대기 요청
              </Typography>
              <Paper variant="outlined" sx={{ mb: 2 }}>
                {reqLoading ? (
                  <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography>요청 목록 불러오는 중...</Typography>
                  </Box>
                ) : reqs.length === 0 ? (
                  <Box sx={{ p: 2, color: "#888" }}>대기 요청이 없습니다.</Box>
                ) : (
                  <TableContainer sx={{ bgcolor: "#1e1e1e" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: "#ccc" }}>요청자</TableCell>
                          <TableCell sx={{ color: "#ccc" }}>요청일</TableCell>
                          <TableCell sx={{ color: "#ccc" }} align="right">
                            처리
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reqs.map((r) => (
                          <TableRow key={`${r.module_key}-${r.requester_user_id}`} hover>
                            <TableCell sx={{ color: "#fff" }}>{r.requester_user_id}</TableCell>
                            <TableCell sx={{ color: "#ccc" }}>{r.requested_at}</TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<CheckIcon />}
                                  disabled={
                                    approveBusy === r.requester_user_id ||
                                    rejectBusy === r.requester_user_id
                                  }
                                  onClick={() =>
                                    openConfirm(
                                      `승인할까요? (${r.requester_user_id})`,
                                      () => approveUser(m.module_key, r.requester_user_id)
                                    )
                                  }
                                >
                                  {approveBusy === r.requester_user_id ? "승인 중..." : "승인"}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="inherit"
                                  startIcon={<CloseIcon />}
                                  disabled={
                                    approveBusy === r.requester_user_id ||
                                    rejectBusy === r.requester_user_id
                                  }
                                  onClick={() =>
                                    openConfirm(
                                      `거절할까요? (${r.requester_user_id})`,
                                      () => rejectUser(m.module_key, r.requester_user_id)
                                    )
                                  }
                                >
                                  {rejectBusy === r.requester_user_id ? "거절 중..." : "거절"}
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>

              {/* Approved Users */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                승인된 사용자
              </Typography>
              <Paper variant="outlined">
                {apprLoading ? (
                  <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography>승인 사용자 목록 불러오는 중...</Typography>
                  </Box>
                ) : apprs.length === 0 ? (
                  <Box sx={{ p: 2, color: "#888" }}>승인된 사용자가 없습니다.</Box>
                ) : (
                  <TableContainer sx={{ bgcolor: "#1e1e1e" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: "#ccc" }}>사용자</TableCell>
                          <TableCell sx={{ color: "#ccc" }}>승인일</TableCell>
                          <TableCell sx={{ color: "#ccc" }} align="right">
                            추방
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {apprs.map((u) => (
                          <TableRow key={`${u.module_key}-${u.user_id}`} hover>
                            <TableCell sx={{ color: "#fff" }}>{u.user_id}</TableCell>
                            <TableCell sx={{ color: "#ccc" }}>{u.approved_at}</TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  openConfirm(
                                    `정말 추방할까요? (${u.user_id})`,
                                    () => evictUser(m.module_key, u.user_id)
                                  )
                                }
                                disabled={evictBusy === u.user_id}
                              >
                                <DeleteIcon
                                  sx={{
                                    color: evictBusy === u.user_id ? "#bbb" : "#f44336",
                                  }}
                                />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Paper>
          );
        })
      )}
    </Paper>
  );

  /** ================== Render ================== */
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="caption" color="text.secondary">
        * 본 탭은 Hub Key 및 MCP 접근 권한의 셀프 관리를 위한 화면입니다.
      </Typography>
    <Divider sx={{ mb: 2 }} />
      {(globalLoading || loadingHub || loadingRequested || loadingApproved || loadingOwnerModules) && (
        <LoadingBanner text="데이터를 불러오는 중..." />
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {info}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ===== Left Tabs Layout ===== */}
      <Box sx={{ display: "flex", gap: 2 }}>
        {/* Left vertical tabs */}
        <Paper variant="outlined" sx={{ width: 220, height: "fit-content" }}>
          <Tabs
            orientation="vertical"
            variant="scrollable"
            value={leftTab}
            onChange={(_, v) => setLeftTab(v)}
            sx={{ borderRight: 1, borderColor: "divider" }}
          >
            <Tab label="Hub Key" />
            <Tab label="Own MCP" />
            <Tab label="MCP Request" />
          </Tabs>
        </Paper>

        {/* Right content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {leftTab === 0 && HubKeySection}
          {leftTab === 1 && OwnerSection}
          {leftTab === 2 && MyAccessSection}
        </Box>
      </Box>

      {/* ===== Confirm Dialog ===== */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>확인</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmMsg}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="inherit">
            취소
          </Button>
          <Button onClick={runConfirm} variant="contained">
            확인
          </Button>
        </DialogActions>
      </Dialog>


    </Box>
  );
}
